import React from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, Box, Typography, Chip, LinearProgress } from '@mui/material';

const FeatureSection = ({ features, maxFrequency, showQuotes = true }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {features.map((feature, index) => (
      <Box key={index}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {feature.name}
            </Typography>
            <Chip
              label={feature.strategic_value}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.75rem',
                bgcolor: feature.strategic_value === 'high' ? 'success.lighter' : 'info.lighter',
                color: feature.strategic_value === 'high' ? 'success.dark' : 'info.dark',
                borderColor: feature.strategic_value === 'high' ? 'success.light' : 'info.light'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              variant="determinate"
              value={(feature.frequency / maxFrequency) * 100}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'success.main',
                  borderRadius: 1
                }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 56 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {feature.frequency}
            </Typography>
          </Box>
        </Box>

        {showQuotes && feature.quotes && feature.quotes[0] && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              ml: 2,
              fontStyle: 'italic',
              borderLeft: '2px solid',
              borderColor: 'divider',
              pl: 2
            }}
          >
            &quot;{feature.quotes[0]}&quot;
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

const DelightFeatures = ({ showQuotes = true }) => {
  const delight_features = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.delight_features);

  if (!delight_features) return null;

  // Format delight features
  const features = Object.entries(delight_features).map(([name, data]) => ({
    name,
    ...data
  }));

  // Find max frequency for percentage calculation
  const maxFrequency = Math.max(...features.map((f) => f.frequency));

  // Sort by frequency
  const sortedFeatures = [...features].sort((a, b) => b.frequency - a.frequency);

  return (
    <Card sx={{ width: '100%', mt: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Most Appreciated Features
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Features that users particularly love and value
        </Typography>

        <Box sx={{ mt: 4 }}>
          <FeatureSection features={sortedFeatures} maxFrequency={maxFrequency} showQuotes={showQuotes} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default DelightFeatures;
